// Per-game life-total line chart for the playtest match view.
//
// One small chart per game in a match (bo3 → 3 stacked charts). Two lines
// per chart: Deck A vs Deck B life totals over Forge turn boundaries.
//
// Built on visx primitives — `LinePath` + `curveMonotoneX` for smoothing
// (which never overshoots zero, important when life dips below 0 on a
// killing blow), `scaleLinear` for x/y, and our shared editorial chart
// primitives in `./chart/` for axes, tooltip, and hover state.
//
// Design brief (binding): no chart frame, no top/right axis, no tick marks,
// dashed horizontal gridlines at clean tick values (0/5/10/15/20), solid
// baseline at 20 (game start), sparse vertical guides at every 5 turns,
// hover-only dots, dashed vertical cursor on hover, end-of-game labels
// in line color (no background pill), borderless tooltip.

import { useMemo } from "react";
import { LinePath } from "@visx/shape";
import { curveMonotoneX } from "@visx/curve";
import { scaleLinear } from "@visx/scale";
import type { LifePoint } from "../../../src/forge/parse-life-events.ts";
import {
  ChartTooltip,
  DUEL_PALETTE,
  HoverCursor,
  INK,
  SIZE,
  STROKE,
  TYPE,
  XAxisLabels,
  YGridLines,
  useHoverBucket,
  type TooltipRow,
} from "./chart";

export interface LifeChartProps {
  games: { game_index: number; winner: "a" | "b" | "draw" | "dnf" | null; points: LifePoint[] }[];
}

export default function LifeChart({ games }: LifeChartProps) {
  if (games.length === 0) return null;
  return (
    <div className="space-y-3">
      {games.map((g) => (
        <GameChart key={g.game_index} game={g} />
      ))}
    </div>
  );
}

function GameChart({ game }: { game: LifeChartProps["games"][number] }) {
  const pts = game.points;

  // Hooks must run unconditionally — wrap any short-circuit logic AFTER
  // hook calls so React's rule-of-hooks stays satisfied across renders
  // where `pts.length < 2`.
  const W = 560;
  const H = 140;
  const PAD_L = 24;
  const PAD_R = 60;
  const PAD_T = 12;
  const PAD_B = 22;
  const innerW = W - PAD_L - PAD_R;
  const innerH = H - PAD_T - PAD_B;

  const maxTurn = pts.length > 0 ? Math.max(...pts.map((p) => p.turn)) : 0;
  const minLifeRaw = pts.length > 0
    ? Math.min(0, ...pts.map((p) => Math.min(p.life_a, p.life_b)))
    : 0;
  const minLife = minLifeRaw;
  const maxLife = 20;

  const xScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [0, Math.max(1, maxTurn)],
        range: [PAD_L, PAD_L + innerW],
      }),
    [maxTurn, innerW],
  );
  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        domain: [minLife, maxLife],
        range: [PAD_T + innerH, PAD_T],
      }),
    [minLife, maxLife, innerH],
  );

  // Y gridlines: 0, 5, 10, 15 (dashed). Baseline at 20 (game start) is
  // rendered as solid via `baselineValue`. If life dipped below zero, also
  // draw a dashed line at the floor so the viewer sees the negative range.
  const yGridValues = useMemo(() => {
    const base = [20, 15, 10, 5, 0];
    if (minLife < 0) {
      // Round to nearest 5 below 0 for a clean tick.
      const floor = Math.floor(minLife / 5) * 5;
      return [...base, floor];
    }
    return base;
  }, [minLife]);

  // X-axis labels: T0, T5, T10, ... + the final turn if it isn't a multiple
  // of 5 (so the viewer sees how long the game went).
  const xTicks = useMemo(() => {
    const out: { x: number; label: string; turn: number }[] = [];
    for (let t = 0; t <= maxTurn; t += 5) {
      out.push({ x: xScale(t), label: `T${t}`, turn: t });
    }
    if (maxTurn % 5 !== 0 && maxTurn > 0) {
      out.push({ x: xScale(maxTurn), label: `T${maxTurn}`, turn: maxTurn });
    }
    return out;
  }, [maxTurn, xScale]);

  // Hover state — anchor by turn index. Use turn as the "bucket" since
  // life points are discrete per turn.
  const { containerRef, hover, setHoverBucket, clearHover, toCss } =
    useHoverBucket({ viewBoxW: W, viewBoxH: H });

  // For hit-testing, we slice the chart into N+1 vertical bands centered
  // on each turn. Hovering inside a band picks the nearest turn's data.
  const bands = useMemo(
    () =>
      pts.map((p, i) => {
        const cx = xScale(p.turn);
        const prevX = i === 0 ? PAD_L : xScale(pts[i - 1]!.turn);
        const nextX = i === pts.length - 1 ? PAD_L + innerW : xScale(pts[i + 1]!.turn);
        const left = (prevX + cx) / 2;
        const right = (cx + nextX) / 2;
        return { turn: p.turn, cx, left, right, point: p };
      }),
    [pts, xScale, innerW],
  );

  const final = pts.length > 0 ? pts[pts.length - 1]! : null;
  const winnerLabel =
    game.winner === "a" ? "Deck A won" :
    game.winner === "b" ? "Deck B won" :
    game.winner === "draw" ? "Draw" :
    game.winner === "dnf" ? "Did not finish" :
    "—";

  if (pts.length < 2) return null;

  return (
    <div className="surface-sunken rounded border border-border px-3 py-2">
      <div className="flex items-center justify-between text-[11px] text-fg-subtle font-mono mb-1">
        <span>Game {game.game_index + 1}</span>
        <span>{winnerLabel} · {pts.length - 1} turns</span>
      </div>
      <div ref={containerRef} className="relative">
        <svg
          viewBox={`0 0 ${W} ${H}`}
          className="w-full h-auto cursor-crosshair"
          role="img"
          aria-label={`Life totals across turns for game ${game.game_index + 1}`}
          onMouseLeave={clearHover}
        >
          {/* Y gridlines + baseline rule at y=20 (game start). */}
          <YGridLines
            values={yGridValues}
            yScale={(v) => yScale(v)}
            x1={PAD_L}
            x2={PAD_L + innerW}
            showLabels
            labelX={PAD_L - 4}
            baselineValue={20}
          />

          {/* Sparse vertical guides at every T5 — OK for time axis (not
              CMC). 6% black so they read as tone, not lines. */}
          {xTicks
            .filter((t) => t.turn > 0 && t.turn % 5 === 0)
            .map((t) => (
              <line
                key={`vg-${t.turn}`}
                x1={t.x}
                x2={t.x}
                y1={PAD_T}
                y2={PAD_T + innerH}
                stroke="currentColor"
                strokeOpacity={0.06}
                strokeWidth={STROKE.grid}
              />
            ))}

          {/* Life lines — `curveMonotoneX` smooths gently and never
              overshoots, which matters when life ticks below 0. */}
          <LinePath
            data={pts}
            x={(p) => xScale(p.turn)}
            y={(p) => yScale(p.life_a)}
            stroke={DUEL_PALETTE.A}
            strokeWidth={STROKE.focal}
            strokeLinecap="butt"
            strokeLinejoin="round"
            curve={curveMonotoneX}
            fill="none"
          />
          <LinePath
            data={pts}
            x={(p) => xScale(p.turn)}
            y={(p) => yScale(p.life_b)}
            stroke={DUEL_PALETTE.B}
            strokeWidth={STROKE.focal}
            strokeLinecap="butt"
            strokeLinejoin="round"
            curve={curveMonotoneX}
            fill="none"
          />

          {/* Hover dots — only at the hovered turn, one per series. */}
          {hover !== null && (() => {
            const band = bands.find((b) => b.turn === hover.bucket);
            if (!band) return null;
            const ax = xScale(band.point.turn);
            const ay = yScale(band.point.life_a);
            const by = xScale(band.point.turn);
            const byY = yScale(band.point.life_b);
            return (
              <g>
                <HoverCursor x={ax} y1={PAD_T} y2={PAD_T + innerH} />
                <circle cx={ax} cy={ay} r={SIZE.hoverDotRadius + SIZE.hoverDotHalo} fill={INK.haloFill} />
                <circle cx={ax} cy={ay} r={SIZE.hoverDotRadius} fill={DUEL_PALETTE.A} />
                <circle cx={by} cy={byY} r={SIZE.hoverDotRadius + SIZE.hoverDotHalo} fill={INK.haloFill} />
                <circle cx={by} cy={byY} r={SIZE.hoverDotRadius} fill={DUEL_PALETTE.B} />
              </g>
            );
          })()}

          {/* End-of-game labels in line color, no pill. 4px gap from line end. */}
          {final && (
            <>
              <text
                x={xScale(final.turn) + SIZE.endLabelGap + 2}
                y={yScale(final.life_a)}
                fontSize={TYPE.axisLabel}
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                fontWeight={600}
                fill={DUEL_PALETTE.A}
                dominantBaseline="middle"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                A: {final.life_a}
              </text>
              <text
                x={xScale(final.turn) + SIZE.endLabelGap + 2}
                y={yScale(final.life_b)}
                fontSize={TYPE.axisLabel}
                fontFamily="ui-monospace, SFMono-Regular, Menlo, monospace"
                fontWeight={600}
                fill={DUEL_PALETTE.B}
                dominantBaseline="middle"
                style={{ fontVariantNumeric: "tabular-nums" }}
              >
                B: {final.life_b}
              </text>
            </>
          )}

          {/* X-axis tick labels (no axis line). */}
          <XAxisLabels
            ticks={xTicks.map((t) => ({ x: t.x, label: t.label }))}
            y={PAD_T + innerH}
            offset={6}
          />

          {/* Hit-target bands — invisible rects centered on each turn. */}
          {bands.map((band) => (
            <rect
              key={`hit-${band.turn}`}
              x={band.left}
              y={PAD_T}
              width={Math.max(1, band.right - band.left)}
              height={innerH}
              fill="transparent"
              onMouseEnter={() => {
                // Anchor tooltip at the higher of the two life lines so
                // it floats above both dots.
                const topLife = Math.max(band.point.life_a, band.point.life_b);
                const css = toCss(band.cx, yScale(topLife));
                setHoverBucket(band.turn, css);
              }}
            />
          ))}
        </svg>

        {hover !== null && (() => {
          const band = bands.find((b) => b.turn === hover.bucket);
          if (!band) return null;
          const rows: TooltipRow[] = [
            { key: "a", swatch: DUEL_PALETTE.A, label: "Deck A", value: String(band.point.life_a) },
            { key: "b", swatch: DUEL_PALETTE.B, label: "Deck B", value: String(band.point.life_b) },
          ].sort((x, y) => Number(y.value) - Number(x.value));
          return (
            <ChartTooltip
              header={`Turn ${band.turn}`}
              rows={rows}
              cx={hover.cx}
              cy={hover.cy}
            />
          );
        })()}
      </div>
    </div>
  );
}

